/**
 * Plain-language opening-hours parser, ported verbatim from
 * `fe-prototype/build/buildData.js::OFFSIDER_HOURS`.
 *
 * Example round-trip:
 *
 *     parse("Mon-Fri 7am-5pm, Sat 8am-12pm")
 *     // → [{day:"Mon",open:"7:00am",close:"5:00pm"},
 *     //    {day:"Tue",open:"7:00am",close:"5:00pm"}, ...,
 *     //    {day:"Sat",open:"8:00am",close:"12:00pm"},
 *     //    {day:"Sun",closed:true}]
 *
 *     summarize(parse("Mon-Fri 7am-5pm, Sat 8am-12pm"))
 *     // → "Mon–Fri 7:00am–5:00pm · Sat 8:00am–12:00pm"
 *
 * Forgiving about dashes ("–", "—", "to"), am/pm casing, day
 * shorthand ("weekdays"/"weekends"/"daily"/"every day"), and the
 * "Sat closed" idiom.
 *
 * The parser is the centrepiece of the live `HoursRow` editor on the
 * Knowledge base tab; the SPA renders the 7-day grid below the input
 * as the visitor types, so confidence in this port matters more than
 * adding features.
 */

/** Canonical 3-letter day labels, in display order (Mon first). */
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** A single day in a parsed schedule. ``closed: true`` means the
 * business is shut that day; otherwise ``open``/``close`` are
 * normalised display strings like ``"7:00am"``. */
export type ScheduleEntry =
  | { day: string; closed: true; open?: undefined; close?: undefined }
  | { day: string; closed?: false; open: string; close: string };

/**
 * Find the index of a day-token within ``DAYS``. Matches by the token
 * starting with the day's lowercase 3-letter abbreviation, so "monday",
 * "mon.", and "Mon" all hit the same row.
 */
function dayIndex(rawTok: string): number {
  const tok = rawTok.trim().toLowerCase();
  if (!tok) return -1;
  return DAYS.findIndex((d) => tok.startsWith(d.toLowerCase()));
}

/**
 * Normalise a time fragment like ``"7am"`` / ``"08:30"`` / ``"5pm"``
 * to the canonical ``"H:MMam|pm"`` display string. Returns ``null``
 * for anything we can't parse (the calling clause is then ignored).
 *
 * "Sensible default for trade hours": when the user omits am/pm we
 * assume 7-11 = am, everything else = pm. Matches the prototype.
 */
function parseTime(rawTok: string): string | null {
  const tok = rawTok.toLowerCase().replace(/\./g, "").replace(/\s+/g, "");
  const m = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/.exec(tok);
  if (!m) return null;
  const h = Number.parseInt(m[1] ?? "", 10);
  if (Number.isNaN(h) || h < 0 || h > 24) return null;
  const min = m[2] ?? "00";
  let ap = m[3];
  if (!ap) ap = h >= 7 && h <= 11 ? "am" : "pm";
  return `${h}:${min}${ap}`;
}

/**
 * Expand a comma-or-range day segment into a deduplicated list of
 * canonical day labels. Understands "weekdays", "weekends", "daily",
 * "every day", "all week", "7 days", literal day ranges
 * ("Mon-Fri"/"mon to fri"), and bare day names. Returns ``[]`` for
 * tokens we don't recognise.
 */
function expandDays(rawSeg: string): string[] {
  let seg = rawSeg
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\band\b/g, ",")
    .replace(/&/g, ",");
  seg = seg
    .replace(/week\s*days?/g, "mon-fri")
    .replace(/week\s*ends?/g, "sat-sun")
    .replace(/every\s*day|everyday|daily|all\s*week|7\s*days/g, "mon-sun");

  const out: string[] = [];
  for (const raw of seg.split(",")) {
    const st = raw.trim();
    if (!st) continue;
    if (st.includes("-")) {
      const [a, b] = st.split("-");
      const ia = dayIndex(a ?? "");
      const ib = dayIndex(b ?? "");
      if (ia >= 0 && ib >= 0) {
        for (let i = ia; i <= ib; i++) {
          const d = DAYS[i];
          if (d) out.push(d);
        }
      }
    } else {
      const i = dayIndex(st);
      if (i >= 0) {
        const d = DAYS[i];
        if (d) out.push(d);
      }
    }
  }
  // Deduplicate preserving insertion order.
  return [...new Set(out)];
}

/**
 * Parse a plain-language opening-hours string into a 7-entry
 * schedule (one row per day, Mon-first). Days with no matching
 * clause come back as ``closed:true`` so the SPA can render every
 * row even on partial inputs (e.g. "Sat 8am-12pm" alone gives a
 * Sat-open / everything-else-closed schedule).
 *
 * Parser rules (matching the prototype):
 *
 * * Pieces are separated by ``,``, ``;``, or newlines.
 * * A piece with no time and no "closed" is a *pending day list*;
 *   it carries over to the next timed clause ("Mon-Fri 7am-5pm" works,
 *   so does "Mon-Fri, 7am-5pm").
 * * "closed" anywhere in a piece marks those days closed.
 * * Otherwise we split day-segment from time-segment at the first
 *   digit, parse both, and persist the time range on each day.
 * * Time ranges may use ``-``, ``–``, ``—``, or "to" as
 *   the separator.
 */
export function parse(str: string | null | undefined): ScheduleEntry[] {
  const map: Record<string, ScheduleEntry> = {};
  const pieces = String(str ?? "")
    .replace(/[;\n]/g, ",")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  let pendingDays: string[] = [];
  for (const piece of pieces) {
    const c = piece.replace(/[–—]/g, "-").replace(/\bto\b/gi, "-");
    const closed = /closed/i.test(c);
    const hasTime = /\d/.test(c);

    // Day-only fragment (no time, not "closed") -- defer to the next
    // timed clause.
    if (!closed && !hasTime) {
      pendingDays.push(...expandDays(c));
      continue;
    }

    let daySeg: string;
    let timeSeg: string;
    if (closed) {
      daySeg = c.replace(/closed/i, "");
      timeSeg = "closed";
    } else {
      const di = c.search(/\d/);
      daySeg = c.slice(0, di);
      timeSeg = c.slice(di);
    }

    const days = [...new Set([...pendingDays, ...expandDays(daySeg)])];
    pendingDays = [];
    if (days.length === 0) continue;
    if (closed) {
      for (const d of days) {
        map[d] = { day: d, closed: true };
      }
      continue;
    }
    const parts = timeSeg
      .split("-")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length < 2) continue;
    const o = parseTime(parts[0] ?? "");
    const cl = parseTime(parts[1] ?? "");
    if (!o || !cl) continue;
    for (const d of days) {
      map[d] = { day: d, open: o, close: cl };
    }
  }

  return DAYS.map((d) => map[d] ?? { day: d, closed: true });
}

/**
 * Group consecutive same-hours days into a tidy summary line:
 *
 *     summarize(parse("Mon-Fri 7am-5pm, Sat 8am-12pm"))
 *     // → "Mon–Fri 7:00am–5:00pm · Sat 8:00am–12:00pm"
 *
 * Closed days are dropped from the summary. An entirely-closed week
 * returns the literal string ``"Closed"``.
 */
export function summarize(schedule: ScheduleEntry[]): string {
  interface Group {
    start: string;
    end: string;
    sig: string;
    closed: boolean;
    open: string | undefined;
    close: string | undefined;
  }
  const groups: Group[] = [];
  for (const s of schedule) {
    const sig = s.closed ? "closed" : `${s.open}-${s.close}`;
    const last = groups[groups.length - 1];
    if (last && last.sig === sig) {
      last.end = s.day;
    } else {
      groups.push({
        start: s.day,
        end: s.day,
        sig,
        closed: Boolean(s.closed),
        open: s.open,
        close: s.close,
      });
    }
  }
  const open = groups
    .filter((g) => !g.closed)
    .map((g) => {
      const dl = g.start === g.end ? g.start : `${g.start}–${g.end}`;
      return `${dl} ${g.open}–${g.close}`;
    });
  return open.length > 0 ? open.join(" · ") : "Closed";
}
